import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, Zap, Lock, Check, Star, Trophy, Target, Flame, Crown, Sparkles, Gift } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

// Level definitions with XP requirements and rewards
const LEVELS = [
  { level: 1, name: 'Starter', xpRequired: 0, color: 'from-gray-400 to-gray-500', icon: '🌱', sticker: null },
  { level: 2, name: 'Saver', xpRequired: 100, color: 'from-green-400 to-emerald-500', icon: '💰', sticker: '💰' },
  { level: 3, name: 'Pro', xpRequired: 300, color: 'from-blue-400 to-cyan-500', icon: '⚡', sticker: '⚡' },
  { level: 4, name: 'Elite', xpRequired: 600, color: 'from-purple-400 to-violet-500', icon: '💎', sticker: '💎' },
  { level: 5, name: 'Master', xpRequired: 1000, color: 'from-orange-400 to-amber-500', icon: '🔥', sticker: '🔥' },
  { level: 6, name: 'Legend', xpRequired: 1500, color: 'from-yellow-400 to-orange-500', icon: '👑', sticker: '👑' },
  { level: 7, name: 'Champion', xpRequired: 2500, color: 'from-pink-400 to-rose-500', icon: '🏆', sticker: '🏆' },
  { level: 8, name: 'Titan', xpRequired: 4000, color: 'from-indigo-400 to-purple-600', icon: '⭐', sticker: '⭐' },
  { level: 9, name: 'Mythic', xpRequired: 6000, color: 'from-cyan-400 to-blue-600', icon: '🌟', sticker: '🌟' },
  { level: 10, name: 'Immortal', xpRequired: 10000, color: 'from-amber-300 via-yellow-400 to-orange-500', icon: '✨', sticker: '✨' }
];

// Achievement stickers that can be earned
const ACHIEVEMENT_STICKERS = [
  { id: 'first_group', name: 'Group Creator', sticker: '🎯', description: 'Create your first group', xpReward: 30 },
  { id: 'first_expense', name: 'Expense Tracker', sticker: '📝', description: 'Add your first expense', xpReward: 10 },
  { id: 'first_payment', name: 'First Pay', sticker: '💳', description: 'Make your first contribution', xpReward: 20 },
  { id: 'week_warrior', name: 'Week Warrior', sticker: '🔥', description: '7-day contribution streak', xpReward: 50 },
  { id: 'diamond_hand', name: 'Diamond Hand', sticker: '💎', description: '30-day contribution streak', xpReward: 200 },
  { id: 'star_saver', name: 'Star Saver', sticker: '⭐', description: 'Earn 100+ reward points', xpReward: 100 },
  { id: 'social_butterfly', name: 'Social Butterfly', sticker: '🦋', description: 'Join 5 different groups', xpReward: 75 },
  { id: 'big_spender', name: 'Big Spender', sticker: '💸', description: 'Track ₹10,000+ in expenses', xpReward: 100 },
  { id: 'team_player', name: 'Team Player', sticker: '🤝', description: 'Settle 10 payments', xpReward: 80 },
  { id: 'early_bird', name: 'Early Bird', sticker: '🐦', description: 'Pay before reminder', xpReward: 25 },
  { id: 'chat_champion', name: 'Chat Champion', sticker: '💬', description: 'Send 50 messages', xpReward: 40 },
  { id: 'referral_king', name: 'Referral King', sticker: '👑', description: 'Refer 3 friends', xpReward: 150 }
];

// XP earning activities
const XP_ACTIVITIES = [
  { action: 'Sign up', xp: 50, icon: '🎉' },
  { action: 'Create a group', xp: 30, icon: '🎯' },
  { action: 'Join a group', xp: 20, icon: '👥' },
  { action: 'Add an expense', xp: 10, icon: '📝' },
  { action: 'Settle a payment', xp: 15, icon: '💳' },
  { action: 'Verify a contribution', xp: 20, icon: '✅' },
  { action: 'Send a chat message', xp: 2, icon: '💬' },
  { action: 'Daily login bonus', xp: 5, icon: '📅' },
  { action: 'Complete a savings goal', xp: 100, icon: '🏆' }
];

const Gamification = ({ user }) => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(user);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const res = await axios.get(`${API}/users/${user.id}`);
      setUserData(res.data);
      localStorage.setItem('user', JSON.stringify(res.data));
    } catch (error) {
      console.error('Failed to fetch user data');
    } finally {
      setLoading(false);
    }
  };

  const currentXP = userData?.xp || 0;
  const currentLevel = userData?.level || 1;
  const earnedBadges = userData?.badges || [];
  const earnedStickers = userData?.stickers || [];

  // Find current and next level
  const currentLevelData = LEVELS.find(l => l.level === currentLevel) || LEVELS[0];
  const nextLevelData = LEVELS.find(l => l.level === currentLevel + 1);
  
  // Calculate progress to next level
  const xpForCurrentLevel = currentLevelData.xpRequired;
  const xpForNextLevel = nextLevelData?.xpRequired || currentLevelData.xpRequired;
  const xpProgress = nextLevelData 
    ? ((currentXP - xpForCurrentLevel) / (xpForNextLevel - xpForCurrentLevel)) * 100
    : 100;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950">
      {/* Header */}
      <div className="bg-card/80 backdrop-blur-xl border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Current Level Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-3xl bg-gradient-to-br ${currentLevelData.color} p-8 text-white mb-8 relative overflow-hidden`}
        >
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30"></div>
          
          <div className="relative flex items-center gap-6">
            <div className="text-7xl animate-float">{currentLevelData.icon}</div>
            <div className="flex-1">
              <p className="text-sm opacity-90 mb-1">Current Level</p>
              <h1 className="text-4xl font-bold mb-2">Level {currentLevel}: {currentLevelData.name}</h1>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  <span className="text-2xl font-bold">{currentXP} XP</span>
                </div>
                {nextLevelData && (
                  <span className="text-sm opacity-75">
                    {xpForNextLevel - currentXP} XP to Level {nextLevelData.level}
                  </span>
                )}
              </div>
              
              {nextLevelData && (
                <div className="mt-4">
                  <div className="w-full h-3 bg-white/20 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(xpProgress, 100)}%` }}
                      transition={{ duration: 1, ease: "easeOut" }}
                      className="h-full bg-white rounded-full"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Level Roadmap */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card rounded-2xl p-6 border border-border mb-8"
        >
          <div className="flex items-center gap-2 mb-6">
            <Trophy className="w-5 h-5 text-yellow-600" />
            <h2 className="text-xl font-bold">Level Roadmap</h2>
          </div>

          <div className="space-y-4">
            {LEVELS.map((level, index) => {
              const isUnlocked = currentLevel >= level.level;
              const isCurrent = currentLevel === level.level;
              
              return (
                <motion.div
                  key={level.level}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
                    isCurrent 
                      ? 'bg-gradient-to-r from-violet-100 to-purple-100 dark:from-violet-900/30 dark:to-purple-900/30 border-2 border-violet-400'
                      : isUnlocked 
                        ? 'bg-muted/50' 
                        : 'bg-muted/20 opacity-60'
                  }`}
                >
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${level.color} flex items-center justify-center text-2xl ${!isUnlocked && 'grayscale'}`}>
                    {isUnlocked ? level.icon : <Lock className="w-6 h-6 text-white/50" />}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">Level {level.level}: {level.name}</h3>
                      {isCurrent && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-violet-600 text-white">Current</span>
                      )}
                      {isUnlocked && !isCurrent && (
                        <Check className="w-4 h-4 text-green-600" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{level.xpRequired} XP required</p>
                  </div>

                  {level.sticker && (
                    <div className={`text-center ${!isUnlocked && 'opacity-40'}`}>
                      <div className="text-3xl mb-1">{level.sticker}</div>
                      <p className="text-xs text-muted-foreground">
                        {isUnlocked ? 'Unlocked!' : 'Locked'}
                      </p>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Achievement Stickers */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card rounded-2xl p-6 border border-border mb-8"
        >
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="w-5 h-5 text-pink-600" />
            <h2 className="text-xl font-bold">Achievement Stickers</h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {ACHIEVEMENT_STICKERS.map((achievement, index) => {
              const isEarned = earnedBadges.includes(achievement.id) || earnedStickers.includes(achievement.id);
              
              return (
                <motion.div
                  key={achievement.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className={`p-4 rounded-xl text-center transition-all ${
                    isEarned 
                      ? 'bg-gradient-to-br from-violet-100 to-pink-100 dark:from-violet-900/30 dark:to-pink-900/30 border-2 border-violet-300'
                      : 'bg-muted/50 opacity-60'
                  }`}
                >
                  <div className={`text-4xl mb-2 ${!isEarned && 'grayscale opacity-50'}`}>
                    {isEarned ? achievement.sticker : '🔒'}
                  </div>
                  <h3 className="font-semibold text-sm mb-1">{achievement.name}</h3>
                  <p className="text-xs text-muted-foreground mb-2">{achievement.description}</p>
                  <div className="flex items-center justify-center gap-1 text-xs">
                    <Zap className="w-3 h-3 text-yellow-600" />
                    <span className="font-semibold">+{achievement.xpReward} XP</span>
                  </div>
                  {isEarned && (
                    <div className="mt-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                        ✓ Earned
                      </span>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* How to Earn XP */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-card rounded-2xl p-6 border border-border"
        >
          <div className="flex items-center gap-2 mb-6">
            <Gift className="w-5 h-5 text-green-600" />
            <h2 className="text-xl font-bold">How to Earn XP</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {XP_ACTIVITIES.map((activity, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.03 }}
                className="flex items-center gap-3 p-3 rounded-xl bg-muted/50"
              >
                <span className="text-2xl">{activity.icon}</span>
                <div className="flex-1">
                  <p className="text-sm font-medium">{activity.action}</p>
                </div>
                <div className="flex items-center gap-1 text-sm font-bold text-violet-600">
                  <Zap className="w-4 h-4" />
                  +{activity.xp}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Gamification;
